import { LOGOUT_STATUS } from '@/enum/extensionStatuses'
import { ALL_QUEUE_ID } from '@/enum/queueDashboardStatistics'

/**
 * Represents the user information.
 * @typedef {Object} User
 * @property {number} UserID - The unique ID of the user.
 * @property {number} ExtenID - The extension ID associated with the user.
 * @property {boolean} LoginRequired - Indicates if login is required for the user.
 */

/**
 * Represents the recording details of a call.
 * @typedef {Object} Recording
 * @property {string} Filename - The name of the file.
 * @property {string} Options - Recording options used.
 * @property {string} ApproximateURL - The URL to access the recording.
 * @property {number} IsMuted - Indicates if the recording is muted.
 */

/**
 * Represents details of a call.
 * @typedef {Object} Call
 * @property {number} callStarted - Epoch time when the call started.
 * @property {number} calldurationinterval - Duration interval of the call.
 * @property {number} callAnswered - Epoch time when the call was answered.
 * @property {number} answered - Indicates if the call was answered.
 * @property {string} callername - Name of the caller.
 * @property {string} callerphone - Phone number of the caller.
 * @property {string} callstatus - Status of the call (e.g., "Talking").
 * @property {Object} customdata - Custom data associated with the call.
 * @property {string} direction - Direction of the call (e.g., "Incoming").
 * @property {string} ivrid - IVR identifier.
 * @property {Recording} recording - Recording details of the call.
 * @property {string} did - DID associated with the call.
 * @property {string} ip - IP address associated with the call.
 * @property {number} blcServerID - BLC server ID.
 * @property {boolean} isOpensips - Indicates if Opensips is used.
 * @property {string} channel - Channel information.
 * @property {string} channel2 - Secondary channel information.
 * @property {number} queueID - Queue ID.
 */

/**
 * Represents a summary of the user extension.
 * @typedef {Object} Summary
 * @property {string} representative - Name of the representative.
 */

/**
 * Represents the extensions of a user.
 * @typedef {Object} UserExtension
 * @property {Call[]} calls - Array of calls associated with the user.
 * @property {number} userID - The user's ID.
 * @property {string} userName - The name of the user.
 * @property {number} number - Number associated with the user.
 * @property {string} extenUser - Extension user.
 * @property {number} accountID - Account ID.
 * @property {number} topAccountID - Top account ID.
 * @property {Summary} summery - Summary of the user extension.
 * @property {number} onlineUserID - Online user ID.
 * @property {number} representative - Representative's ID.
 * @property {number} representativeStatus - Status of the representative.
 * @property {number} lastCallEventEpoch - Last call event epoch.
 * @property {number} lastAnsweredCallEventEpoch - Last answered call event epoch.
 * @property {number} lastHangupCallEpoch - Last hangup call epoch.
 * @property {number} representativeUpdated - Epoch time of representative update.
 * @property {?string} peerStatus - Peer status.
 * @property {Call} currentCall - Current call details.
 * @property {Object} lastEvent - Last event details.
 */

/**
 * Main structure representing user data and their extensions.
 * @typedef {Object} UserData
 * @property {User} user - User details.
 * @property {UserExtension[]} userExtensions - Array of user extensions.
 * @property {string} userName - The name of the user from first extension.
 */

/**
 * Represents the configuration or status of a system or process.
 * @typedef {Object} AccountStatus
 * @property {number} AlertSecLimit - The time limit in seconds after which an alert is triggered.
 * @property {string} ColorCode - The hexadecimal color code associated with this status.
 * @property {number} GetQueueCalls - Indicates whether to retrieve queue calls (1 for true, 0 for false).
 * @property {string} Name - The name of the status or configuration.
 * @property {number} StatusID - The unique identifier for the status.
 * @property {number} WarningSecLimit - The time limit in seconds after which a warning is triggered.
 */

export default {
    props: {
        queue: {
            type: Object,
            required: true
        },
        queueRuleID: {
            type: [Number, String],
            default: null
        },
        allQueues: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        /**
         * If the component is rendered in table as 'All' row
         */
        isAll() {
            return this.queue.queue_id === ALL_QUEUE_ID
        },
        /**
         * @returns {UserExtension[]} All the realtime extensions
         */
        allRealtimeExtensions() {
            return this.$store.state.extensions.extensions
        },
        /**
         * The list of all members assigned to the queue. If queue is 'All' then it will return all the members from all the queues
         *
         * @returns {User[]} The list of all members assigned to the queue
         */
        queueMembersList() {
            let queueMembersList = []

            if (this.isAll) {
                this.allQueues.forEach(queue => {
                    if (queue.QueueMembers && queue.QueueMembers.length) {
                        queueMembersList.push(...queue.QueueMembers)
                    }
                })
            } else {
                queueMembersList = this.queue.QueueMembers || []
            }

            return queueMembersList
        },
        /**
         * The map of all the members assigned to the queue. The key is the user id and the value is the object with user data and user extensions
         *
         * @returns {{ [key: string]: UserData }}
         */
        queueMembersMap() {
            return this.queueMembersList.reduce(
                (acc, item) => {
                    if (!item.ExtenID) {
                        return acc
                    }

                    const userExtensions = this.allRealtimeExtensions.filter(ext => ext.representative === item.UserID)

                    if (!userExtensions.length) {
                        return acc
                    }

                    acc[item.UserID] = {
                        user: item,
                        userName: userExtensions[0].summery.representative,
                        userExtensions: userExtensions
                    }

                    return acc
                },
                {}
            )
        },

        /**
         * All the calls which are assigned to the queue and are answered by checking the queue id parameter of the call
         *
         * @returns {Call[]}
         */
        activeQueueCalls() {
            const allExtensionsCalls = this.allRealtimeExtensions.map(extension => extension.calls).flat()

            return allExtensionsCalls.filter(call => call.queueID === this.queue.queue_id && !!call.answered)
        },

        /**
         * All the agents which are assigned to the queue and are available
         *
         * The agent is available if:
         * - The agent passed the availability check by calls
         * - The agent passed the availability check by status
         *
         * @returns {UserData[]}
         */
        assignedAndAvailableAgents() {
            return Object.values(this.queueMembersMap).filter((member) => {
                return this.isQueueMemberAvailableByCalls(member) && this.isQueueMemberAvailableByStatus(member)
            })
        },

        /**
         * All the agents which are assigned to the queue and are unavailable
         *
         * The agent is unavailable if:
         * - The agent is not available by calls
         * - The agent is not available by status
         *
         * @returns {UserData[]}
         */
        assignedAndUnavailableAgents() {
            return Object.values(this.queueMembersMap).filter((member) => {
                return !this.isQueueMemberAvailableByCalls(member) || !this.isQueueMemberAvailableByStatus(member)
            })
        },
    },
    methods: {
        /**
         * Get the first extension of the user
         *
         * @param {UserData} queueMember
         * @returns {(UserExtension | null)}
         */
        getQueueMemberFirstExtension(queueMember) {
            const userExtensions = queueMember.userExtensions

            if (!userExtensions || !userExtensions.length) {
                return null
            }

            return userExtensions[0]
        },
        /**
         * Get the status of the queue member
         *
         * @param {UserData} queueMember
         * @returns {(AccountStatus | null)}
         */
        getQueueMemberStatus(queueMember) {
            const userFirstExtension = this.getQueueMemberFirstExtension(queueMember)

            if (!userFirstExtension) {
                return null
            }

            const queueMemberStatus = userFirstExtension.representativeStatus

            return this.$store.getters['entities/getStatusById'](queueMemberStatus)
        },
        /**
         * Checks if a queue member is available based on their status. The method evaluates the availability
         * based on the following criteria:
         *
         * - The user must not be in a 'logged out' status.
         * - Then, one of the following conditions must be met for the user to be considered available:
         *   - If 'LoginRequired' is false, the user is always available (not being in 'logged out' status is sufficient).
         *   - If 'LoginRequired' is true, the user's status must explicitly accept queue calls
         *     (indicated by 'AccountBreaks->GetQueueCalls' being equal to 1).
         *
         * The status is determined from the first user extension, as the user status is consistent across all extensions.
         *
         * @param {UserData} queueMember - The queue member data to evaluate.
         * @returns {boolean} Returns true if the queue member is available, otherwise false.
         */
        isQueueMemberAvailableByStatus(queueMember) {
            const firstUserExtension = this.getQueueMemberFirstExtension(queueMember)

            if (!firstUserExtension || firstUserExtension.representativeStatus === LOGOUT_STATUS) {
                return false
            }

            if (!queueMember.user.LoginRequired) {
                return true
            }

            const queueMemberStatus = this.getQueueMemberStatus(queueMember)

            if (!queueMemberStatus) {
                return false
            }

            return queueMemberStatus.GetQueueCalls === 1
        },
        /**
         * Checks if a queue member is available based on their calls. The method evaluates the availability
         * based on the following criteria:
         *
         * - The user must not have any calls via any of their extensions.
         *
         * @param {UserData} queueMember - The queue member data to evaluate.
         * @returns {boolean} Returns true if the queue member is available, otherwise false.
         */
        isQueueMemberAvailableByCalls(queueMember) {
            const userExtensions = queueMember.userExtensions

            if (!userExtensions || !userExtensions.length) {
                return false
            }

            return !userExtensions.some(extension => extension.calls.length > 0)
        },
    }
}
